import { useEffect, useState } from 'react';

/**
 * B309 fix round 2 — which days have a completion write in flight, and the
 * status that write is about to produce.
 *
 * The done / not done button is unavailable from the press until the week
 * refetch has landed or failed, and the panel shows the new status meanwhile.
 * That state cannot live in the panel: the panel unmounts when the athlete
 * closes it or opens another day, and a panel reopened during the refetch
 * would then offer the old status and let a second write through. So it is
 * keyed by the plan day's id here, outside any component, and every panel for
 * that day reads it.
 *
 * A write is entered by `beginDayWrite` (which refuses a second one for the
 * same day, synchronously) and always left by `endDayWrite`, success or not.
 */

export type DayWriteStatus = 'completed' | 'upcoming';
export type DayWrite = { status: DayWriteStatus };

const writes = new Map<string, DayWrite>();
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Claim the day for a write. False when one is already in flight (do nothing). */
export function beginDayWrite(dayId: string, status: DayWriteStatus): boolean {
  if (writes.has(dayId)) return false;
  writes.set(dayId, { status });
  emit();
  return true;
}

/** Release the day, whatever the outcome. */
export function endDayWrite(dayId: string): void {
  if (writes.delete(dayId)) emit();
}

/**
 * The in-flight write for a day, or undefined. Re-renders the caller on change.
 *
 * Plain state fed by a subscription, not `useSyncExternalStore`: a store
 * change there re-renders synchronously with the parent's props as last
 * committed, so the moment a write is released (right after the refetch has
 * landed) the panel would flash the OLD status for a frame before the parent's
 * own pending update arrived. A state update from a listener goes into the
 * same batch as the refetch's own state updates, so both land in one render.
 */
export function useDayWrite(dayId: string | undefined): DayWrite | undefined {
  const [write, setWrite] = useState<DayWrite | undefined>(() =>
    dayId ? writes.get(dayId) : undefined,
  );
  useEffect(() => {
    const read = () => setWrite(dayId ? writes.get(dayId) : undefined);
    read();
    return subscribe(read);
  }, [dayId]);
  return write;
}

/** Test-only: forget every in-flight write so one test cannot leak into the next. */
export function __resetDayWritesForTests(): void {
  writes.clear();
  emit();
}
